<script setup lang="ts">
import TripCardLoader from '../card-section/sections/TripCardLoader.vue';
import TripCardSection from '../card-section/sections/TripCardSection.vue';
import { useHandleEvents } from '@composable/_local/hotel&view/useHandleEvents';
import type TripPackage from '@itinerary/trip/TripPackage';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import MrSimpleAccordion from '@components/base/mr-simple-accordion/MrSimpleAccordion.vue';
import type TripModule from '@itinerary/trip/TripModule';
import MrLoading from '@components/base/mr-loading/MrLoading.vue';

const emit = defineEmits<{
    viewPromotedList: [];
    viewReservableList: [];
    viewNonReservableList: [];
}>();

const AutoReserveCardSection = defineAsyncComponent({
    loader: () => import('../card-section/sections/AutoReserveCardSection.vue'),
    loadingComponent: MrLoading,
    suspensible: false,
});

const RouteCardSection = defineAsyncComponent({
    loader: () => import('../card-section/sections/RouteCardSection.vue'),
    suspensible: false,
});

const itineraryStore = useItineraryStore();

const siteConfigs = useSiteConfigs();

const { selectPackage } = usePackageSelectStore();
const handleEvents = useHandleEvents;

const {
    reservableList,
    lowestPricePromoted,
    promotedList,
    isLoadedSuccess,
    nonReservableList,
    hasAutoReserve,
    loading,
    listTips,
} = storeToRefs(itineraryStore);
const currentModule = useCurrentItineraryModule<TripModule>();
const { currentLoadingStatus, isRealTimeLoading } = moduleToRefs(
    currentModule.value
);
const { isRouteEnabled } = moduleToRefs(currentModule.value);

const layoutStore = useLayoutStore();
const { isExtraFeaturesEnabled, normalSupportNumber } =
    classToRefs(layoutStore);
const isMounted = useMounted();
const autoReserveContainer: Ref<HTMLElement | null> = ref(null);
const isAutoReserveVisible: Ref<boolean> = ref(false);

const isLoading = computed(
    () =>
        loading.value ||
        currentLoadingStatus.value === undefined ||
        currentLoadingStatus.value === LoadingStatus.None ||
        !process.client ||
        !isMounted.value
);

const route = useRoute();

const reserveItem = async (
    item: TripPackage,
    index: number,
    bypassRaise?: boolean
) => {
    if (!item.reservable) {
        return;
    }
    if (item.callToReserve) {
        location.href = `tel:+9821${normalSupportNumber.value}`;
        return;
    }
    if (item.reservationUrl) {
        window.open(item.reservationUrl, '_blank');
        return;
    }
    handleEvents(item, index);
    item.loadBaggage();
    const { endOfSelection } = await selectPackage({
        index: itineraryStore.currentSetIndex,
        package: item,
        bypassRaise,
    });
    if (!endOfSelection) {
        scrollTo({ top: 0, behavior: 'smooth' });
    }
};

watch(loading, value => {
    if (!value) {
        const { key } = route.query;
        if (key) {
            const reserveItemIndex = reservableList.value.findIndex(
                ip => ip.uniqueId === key
            );
            if (reserveItemIndex !== -1) {
                const reserveItemValue = reservableList.value[reserveItemIndex];
                reserveItem(
                    reserveItemValue as TripPackage,
                    reserveItemIndex,
                    true
                );
            }
        }
    }
});

watch(autoReserveContainer, val => {
    // just for better load
    if (val) {
        const observer = new IntersectionObserver(
            entries => {
                // Loop over the entries provided by the observer
                entries.forEach(entry => {
                    // If the target element is intersecting (visible)
                    if (entry.isIntersecting) {
                        isAutoReserveVisible.value = true;
                        // Stop observing once it's visible to save resources
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '0px', // When target comes into view
                threshold: 0.1, // Trigger when 10% of the target is visible
            }
        );

        // Start observing our container element
        observer.observe(val);
    }
});
</script>
<template>
    <TripCardLoader v-if="isLoading" :count="4" v-bind="$attrs" />
    <template v-else>
        <TripCardSection
            v-if="lowestPricePromoted && isLoadedSuccess"
            key="promoted-list"
            class="promoted-cart-container"
            :list="[lowestPricePromoted] as TripPackage[]"
            v-bind="$attrs"
            @view-section="emit('viewPromotedList')"
            @button-clicked="reserveItem"
        />
        <TripCardSection
            v-if="promotedList.length && isLoadedSuccess"
            key="promoted-list"
            class="promoted-cart-container"
            :title="`پیشنهاد ویژه ${siteConfigs.title}`"
            :list="promotedList as TripPackage[]"
            v-bind="$attrs"
            @view-section="emit('viewPromotedList')"
            @button-clicked="reserveItem"
        />
        <TripCardSection
            key="reservable-list"
            class="cards-container"
            :list="reservableList as TripPackage[]"
            display-banners
            v-bind="$attrs"
            display-price-track
            :display-taxi-offer="isExtraFeaturesEnabled"
            real-time-loading
            @button-clicked="reserveItem"
            @view-section="emit('viewReservableList')"
        >
            <template #extra-item>
                <mr-simple-accordion
                    v-for="(
                        { order, title, description, iconImg, additionalInfo },
                        index
                    ) in listTips"
                    :key="`${order}-${title}-${index}`"
                    :title
                    :style="{ order }"
                    class="accordion-wrapper"
                >
                    <template
                        v-if="iconImg"
                        #title="{ title: headerTitle, expanded }"
                    >
                        <div class="tip-header" :class="{ expanded }">
                            <component
                                :is="iconImg"
                                width="40px"
                                height="40px"
                                class="tip-logo"
                            />
                            <mr-typography
                                :size="{
                                    desktop: 'large',
                                    mobile: 'medium',
                                }"
                                weight="700"
                                color="var(--cool-gray-900)"
                            >
                                {{ headerTitle }}
                            </mr-typography>
                        </div>
                    </template>
                    <template #description>
                        <div class="tips-description">
                            <mr-typography
                                white-space="pre-line"
                                class="description"
                                color="var(--cool-gray-900)"
                            >
                                {{ description }}
                            </mr-typography>
                            <div v-if="additionalInfo" class="additional-info">
                                <div class="label-container">
                                    <mr-icon
                                        :icon="additionalInfo.icon"
                                        color="var(--cool-gray-500)"
                                    />
                                    <mr-typography
                                        weight="500"
                                        size="large"
                                        color="var(--cool-gray-700)"
                                    >
                                        {{ additionalInfo.label }}
                                    </mr-typography>
                                </div>
                                <mr-typography
                                    weight="700"
                                    size="large"
                                    color="var(--cool-gray-900)"
                                >
                                    {{ additionalInfo.content }}
                                </mr-typography>
                            </div>
                        </div>
                    </template>
                </mr-simple-accordion>
            </template>
        </TripCardSection>
        <ClientOnly>
            <Transition>
                <RouteCardSection
                    v-if="isRouteEnabled"
                    v-bind="$attrs"
                    @button-clicked="reserveItem"
                />
            </Transition>
            <div ref="autoReserveContainer" v-bind="$attrs">
                <AutoReserveCardSection
                    v-if="
                        hasAutoReserve &&
                        !isRealTimeLoading &&
                        isAutoReserveVisible
                    "
                    v-bind="$attrs"
                />
            </div>
        </ClientOnly>
        <TripCardSection
            v-if="
                nonReservableList.length && currentLoadingStatus === 'success'
            "
            key="none-reservable-list"
            v-bind="$attrs"
            :title="
                hasAutoReserve ? 'غیرقابل رزرو' : 'غیرقابل رزرو (تکمیل ظرفیت)'
            "
            :list="nonReservableList as TripPackage[]"
            disable-details
            @view-section="emit('viewNonReservableList')"
        />
    </template>
</template>
<style scoped lang="scss">
.accordion-wrapper {
    padding: 8px 12px;
    @include desktop-only {
        padding: 12px 32px;
    }
}
.tip-header {
    display: flex;
    align-items: center;
    gap: 12px;

    .tip-logo {
        height: 40px;
        width: 40px;
    }
    &.expanded {
        padding-bottom: 12px;
    }
}

.tips-description {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .additional-info {
        display: flex;
        gap: 8px;
        align-items: start;
        flex-wrap: wrap;

        .label-container {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        @include desktop-only {
            flex-direction: row;
            gap: 8px;
            align-items: center;
        }
    }
}

.promoted-cart-container {
    :deep(.trip-card-wrapper) {
        border: 1px solid usePalette();
    }
}
</style>
